import React, { useRef, useState } from 'react';
import { Download, FileSpreadsheet, Upload, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import api from '../services/api';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

/**
 * Excel Import card.
 * Props:
 *   entity: 'brands' | 'products'
 *   onImported: () => void   // callback to reload parent data
 */
const ExcelImport = ({ entity, onImported }) => {
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);

  const label = entity === 'brands' ? 'marcas' : 'productos';
  const templatePath = `/templates/${entity}.xlsx`;
  const importPath = `/import/${entity}`;
  const filename = `plantilla_${label}.xlsx`;

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const res = await api.get(templatePath, {
        responseType: 'blob',
        headers: { Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      });

      // Validate blob size
      const blob = res.data;
      if (!blob || blob.size === 0) {
        throw new Error('El archivo descargado está vacío');
      }

      // Create object URL and trigger download
      const url = window.URL.createObjectURL(
        new Blob([blob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      );

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.rel = 'noopener';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 200);

      toast({
        title: 'Plantilla descargada',
        description: `${filename} guardado en tu carpeta de descargas.`,
      });
    } catch (err) {
      console.error('[ExcelImport] download error:', err);
      // When responseType is blob and server returned JSON error, parse it
      let detail = err?.message || 'Intenta de nuevo.';
      try {
        if (err?.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          detail = parsed.detail || detail;
        } else if (err?.response?.data?.detail) {
          detail = err.response.data.detail;
        }
      } catch (parseErr) {
        console.warn('[ExcelImport] could not parse error blob:', parseErr);
      }
      toast({ title: 'Error al descargar plantilla', description: detail });
    } finally {
      setDownloading(false);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(importPath, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
      toast({
        title: 'Importación completada',
        description: `${data.created} creadas · ${data.updated} actualizadas · ${data.skipped} omitidas.`,
      });
      if (((data.created || 0) + (data.updated || 0)) > 0 && onImported) onImported();
    } catch (err) {
      console.error('[ExcelImport] import error:', err);
      toast({ title: 'Error al importar', description: err?.response?.data?.detail || 'Intenta de nuevo' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
            <FileSpreadsheet className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900">Importar {label} por Excel</h3>
            <p className="mt-1 text-sm text-slate-600">
              Descarga la plantilla, complétala y súbela para crear o actualizar {label} masivamente.
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            variant="outline"
            onClick={downloadTemplate}
            disabled={downloading}
            className="h-10 gap-2 border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-50"
          >
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {downloading ? 'Descargando...' : 'Plantilla'}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <Button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="h-10 gap-2 bg-emerald-700 text-white hover:bg-emerald-800"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? 'Importando...' : 'Subir Excel'}
          </Button>
        </div>
      </div>

      {result && (
        <div className="mt-4 space-y-2 rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="inline-flex items-center gap-1 text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> Creadas: <strong>{result.created}</strong>
            </span>
            <span className="inline-flex items-center gap-1 text-blue-700">
              <CheckCircle2 className="h-4 w-4" /> Actualizadas: <strong>{result.updated}</strong>
            </span>
            <span className="inline-flex items-center gap-1 text-slate-600">
              Omitidas: <strong>{result.skipped}</strong>
            </span>
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-2">
              <div className="mb-1 flex items-center gap-1 text-sm font-medium text-red-700">
                <AlertCircle className="h-4 w-4" /> Errores ({result.errors.length})
              </div>
              <ul className="max-h-40 space-y-0.5 overflow-y-auto rounded bg-red-50 px-3 py-2 text-xs text-red-800">
                {result.errors.map((e, i) => <li key={`err-${i}-${(e || '').slice(0, 40)}`}>• {e}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExcelImport;
